import type { Rng } from '../rng';

/**
 * Was unterwegs passiert.
 *
 * Ohne diese Ereignisse ist die Fahrt Wartezeit — man sieht einem Punkt beim
 * Wandern zu. Mit ihnen ist sie der Ort, an dem Atmosphaere und Figuren leben,
 * und vor allem: an dem die getroffene Entscheidung nochmal auf die Probe
 * gestellt wird.
 *
 * Der wichtigste Fall ist der Sonarkontakt. Abzudrehen und ihn zu untersuchen
 * kostet Stunden — und diese Stunden koennen genau die sein, die den
 * Ueberlebenden am Ziel gefehlt haben. Damit wiederholt sich das Dilemma des
 * Spiels im Kleinen: Erkenntnis gegen Menschenleben, diesmal unter Zeitdruck
 * und ohne Bedenkzeit.
 */

export type VoyageEventKind =
  | 'sonar_contact'
  | 'radio_traffic'
  | 'weather_shift'
  | 'crew_remark'
  | 'second_call'
  | 'mechanical'
  | 'derelict'
  | 'crew_illness'
  | 'floating_debris'
  | 'shadowed'
  | 'bunker_at_sea'
  | 'drifting_mine'
  | 'station_silence'
  | 'stowaway'
  | 'charted_wrong';

export interface VoyageChoice {
  readonly id: string;
  readonly labelKey: string;
  /** Zusaetzliche Stunden, die diese Wahl kostet. */
  readonly delayHours: number;
  readonly fuelCost: number;
  /**
   * Kann diese Wahl eine Anomalie-Signatur einbringen? Nur Sonarkontakt und
   * treibendes Wrack liefern unterwegs Beweise — und nur, wenn man dafuer
   * abdreht.
   */
  readonly yieldsEvidence: boolean;
  /**
   * Menschen, die diese Wahl unmittelbar rettet.
   *
   * Nur der Zweitnotruf. Das ist die haerteste Entscheidung des Spiels im
   * Kleinen: Hier stehen Leben, die man sofort retten kann, gegen Stunden,
   * die am eigentlichen Einsatzort fehlen werden.
   */
  readonly soulsRescued: number;
  /** Zusaetzlicher Rumpfschaden in Prozentpunkten. */
  readonly hullDamage: number;
  readonly outcomeKey: string;
}

export interface VoyageEvent {
  readonly id: string;
  readonly kind: VoyageEventKind;
  readonly textKey: string;
  /** Stelle auf der Route, an der es passiert (0 bis 1). */
  readonly atProgress: number;
  readonly choices: readonly VoyageChoice[];
}

/** Wie viele Textvarianten es je Ereignisart gibt. */
const VARIANTS: Readonly<Record<VoyageEventKind, number>> = {
  sonar_contact: 9,
  radio_traffic: 9,
  weather_shift: 8,
  crew_remark: 13,
  second_call: 6,
  mechanical: 8,
  derelict: 7,
  crew_illness: 5,
  floating_debris: 7,
  shadowed: 5,
  bunker_at_sea: 6,
  drifting_mine: 6,
  station_silence: 5,
  stowaway: 5,
  charted_wrong: 6,
};

/** Relative Haeufigkeit der Ereignisarten. */
/**
 * Relative Haeufigkeit.
 *
 * Der Zweitnotruf ist bewusst selten: Seine Entscheidung wiegt schwer, und
 * eine Entscheidung, die jede Fahrt faellt, nutzt sich ab.
 */
const WEIGHTS: Readonly<Record<VoyageEventKind, number>> = {
  sonar_contact: 4,
  radio_traffic: 3,
  weather_shift: 3,
  crew_remark: 4,
  second_call: 2,
  mechanical: 2,
  derelict: 2,
  crew_illness: 2,
  floating_debris: 3,
  shadowed: 2,
  // Der Bunkerhalt ist selten und deshalb wertvoll: Waere er haeufig, waere
  // die Regel „Nachschub nur an der Station" wirkungslos.
  bunker_at_sea: 1,
  drifting_mine: 2,
  // Die Funkstille der Station ist der schwerste Einwurf im Spiel. Einmal pro
  // Kampagne ist eindrucksvoll, dreimal ist eine Masche.
  station_silence: 1,
  stowaway: 1,
  charted_wrong: 2,
};

/** Alle Ereignisarten. Exportiert, damit Tests gegen die Liste statt gegen
 * eine Zahl pruefen koennen. */
export const EVENT_KINDS = Object.keys(VARIANTS) as VoyageEventKind[];
const KINDS = EVENT_KINDS;

function choicesFor(kind: VoyageEventKind, id: string, rng: Rng): VoyageChoice[] {
  switch (kind) {
    case 'sonar_contact':
      return [
        {
          id: `${id}.investigate`,
          labelKey: 'voyage.choice.investigate',
          // Genug, um ein knappes Zeitfenster reissen zu lassen.
          delayHours: rng.float(2.5, 5),
          fuelCost: rng.float(8, 18),
          yieldsEvidence: true,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.investigate.result`,
        },
        {
          id: `${id}.hold`,
          labelKey: 'voyage.choice.hold_course',
          delayHours: 0,
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.hold.result`,
        },
      ];

    case 'radio_traffic':
      return [
        {
          id: `${id}.listen`,
          labelKey: 'voyage.choice.listen',
          delayHours: rng.float(0.3, 0.8),
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.listen.result`,
        },
        {
          id: `${id}.ignore`,
          labelKey: 'voyage.choice.ignore',
          delayHours: 0,
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.ignore.result`,
        },
      ];

    case 'weather_shift':
      return [
        {
          id: `${id}.ease`,
          labelKey: 'voyage.choice.ease_off',
          delayHours: rng.float(1.5, 3.5),
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.ease.result`,
        },
        {
          id: `${id}.push`,
          labelKey: 'voyage.choice.push_on',
          delayHours: 0,
          fuelCost: rng.float(10, 22),
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: rng.float(2, 6),
          outcomeKey: `${id}.push.result`,
        },
      ];

    case 'second_call':
      return [
        {
          id: `${id}.divert`,
          labelKey: 'voyage.choice.divert',
          // Teuer genug, um ein knappes Zeitfenster am Ziel zu reissen.
          delayHours: rng.float(4, 8),
          fuelCost: rng.float(15, 30),
          yieldsEvidence: false,
          soulsRescued: rng.intInclusive(2, 6),
          hullDamage: 0,
          outcomeKey: `${id}.divert.result`,
        },
        {
          id: `${id}.press_on`,
          labelKey: 'voyage.choice.press_on',
          delayHours: 0,
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.press_on.result`,
        },
      ];

    case 'mechanical':
      return [
        {
          id: `${id}.repair`,
          labelKey: 'voyage.choice.repair',
          delayHours: rng.float(2, 4.5),
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.repair.result`,
        },
        {
          id: `${id}.nurse`,
          labelKey: 'voyage.choice.nurse',
          delayHours: rng.float(0.5, 1.5),
          fuelCost: rng.float(12, 25),
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: rng.float(4, 9),
          outcomeKey: `${id}.nurse.result`,
        },
      ];

    case 'derelict':
      return [
        {
          id: `${id}.board`,
          labelKey: 'voyage.choice.board',
          delayHours: rng.float(3, 6),
          fuelCost: rng.float(5, 12),
          yieldsEvidence: true,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.board.result`,
        },
        {
          id: `${id}.log`,
          labelKey: 'voyage.choice.log',
          delayHours: rng.float(0.2, 0.5),
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.log.result`,
        },
      ];

    /**
     * Jemand an Bord faellt aus.
     *
     * Die einzige Entscheidung, bei der es um die eigene Besatzung geht.
     * Umkehren waere hier keine Option — es geht darum, ob man einen Umweg zu
     * einem Schiff mit Arzt faehrt oder es selbst versucht.
     *
     * Der Umweg zaehlt als Rettung, und das ist keine Buchhaltung: Ohne
     * diesen Gegenwert waere Selbstbehandeln umsonst und damit immer richtig —
     * dieselbe Falle, in die frueher schon die Wahl zwischen Retten und Bergen
     * getappt war.
     */
    case 'crew_illness':
      return [
        {
          id: `${id}.transfer`,
          labelKey: 'voyage.choice.transfer',
          delayHours: rng.float(3.5, 6.5),
          fuelCost: rng.float(10, 20),
          yieldsEvidence: false,
          soulsRescued: 1,
          hullDamage: 0,
          outcomeKey: `${id}.transfer.result`,
        },
        {
          id: `${id}.treat`,
          labelKey: 'voyage.choice.treat',
          delayHours: rng.float(0.5, 1.5),
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.treat.result`,
        },
      ];

    /**
     * Treibgut im Wasser.
     *
     * Billiger als ein Sonarkontakt und deshalb haeufiger: Ein paar Minuten
     * Aufstoppen genuegen. Der Ertrag ist entsprechend unsicher — nicht jedes
     * Stueck Plastik ist ein Fundstueck.
     */
    case 'floating_debris':
      return [
        {
          id: `${id}.recover`,
          labelKey: 'voyage.choice.recover',
          delayHours: rng.float(1.2, 2.5),
          fuelCost: rng.float(2, 6),
          yieldsEvidence: true,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.recover.result`,
        },
        {
          id: `${id}.pass`,
          labelKey: 'voyage.choice.pass',
          delayHours: 0,
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.pass.result`,
        },
      ];

    /**
     * Jemand folgt.
     *
     * Kein Fund, keine Rettung — nur die Frage, ob man sich stellt. Wer
     * abdreht und wartet, verliert Zeit und erfaehrt womoeglich, wer da
     * mitfaehrt. Wer weiterfaehrt, behaelt seinen Vorsprung und die Frage.
     */
    case 'shadowed':
      return [
        {
          id: `${id}.confront`,
          labelKey: 'voyage.choice.confront',
          delayHours: rng.float(2, 4),
          fuelCost: rng.float(6, 14),
          yieldsEvidence: true,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.confront.result`,
        },
        {
          id: `${id}.shake`,
          labelKey: 'voyage.choice.shake',
          delayHours: 0,
          fuelCost: rng.float(8, 16),
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: rng.float(1, 3),
          outcomeKey: `${id}.shake.result`,
        },
      ];

    /**
     * Ein Versorger bietet Treibstoff an.
     *
     * Die Gegenrechnung zu „Nachschub gibt es nur an der Station": Wer draussen
     * bleibt, kommt auch draussen wieder an Treibstoff — aber gegen Stunden,
     * und die fehlen am Ziel. Ohne diesen Ausweg endete jede laengere Zeit auf
     * See zwangslaeufig an der leeren Bunkeranzeige.
     */
    case 'bunker_at_sea':
      return [
        {
          id: `${id}.bunker`,
          labelKey: 'voyage.choice.bunker',
          delayHours: rng.float(3, 6),
          // Negative Kosten sind Gewinn — das Fahrzeug bekommt Treibstoff.
          fuelCost: -rng.float(150, 320),
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.bunker.result`,
        },
        {
          id: `${id}.decline`,
          labelKey: 'voyage.choice.decline',
          delayHours: 0,
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.decline.result`,
        },
      ];

    /** Treibmine oder Grosstreibgut im Kurs. Wegschiessen oder umfahren. */
    case 'drifting_mine':
      return [
        {
          id: `${id}.destroy`,
          labelKey: 'voyage.choice.destroy',
          delayHours: rng.float(1, 2.5),
          fuelCost: rng.float(2, 6),
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: rng.float(2, 7),
          outcomeKey: `${id}.destroy.result`,
        },
        {
          id: `${id}.avoid`,
          labelKey: 'voyage.choice.avoid',
          delayHours: rng.float(2, 4.5),
          fuelCost: rng.float(6, 14),
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.avoid.result`,
        },
      ];

    /**
     * Die Station antwortet nicht mehr.
     *
     * Der schwerste Einwurf des Spiels — und der einzige, bei dem Umkehren
     * ueberhaupt zur Debatte steht. Es kostet so viel Zeit, dass der Einsatz
     * am Ziel praktisch verloren ist.
     */
    case 'station_silence':
      return [
        {
          id: `${id}.turn_back`,
          labelKey: 'voyage.choice.turn_back',
          delayHours: rng.float(14, 26),
          fuelCost: rng.float(40, 90),
          yieldsEvidence: true,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.turn_back.result`,
        },
        {
          id: `${id}.carry_on`,
          labelKey: 'voyage.choice.carry_on',
          delayHours: 0,
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.carry_on.result`,
        },
      ];

    /** Jemand ist an Bord, der nicht an Bord sein sollte. */
    case 'stowaway':
      return [
        {
          id: `${id}.question`,
          labelKey: 'voyage.choice.question',
          delayHours: rng.float(1.5, 3),
          fuelCost: 0,
          yieldsEvidence: true,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.question.result`,
        },
        {
          id: `${id}.confine`,
          labelKey: 'voyage.choice.confine',
          delayHours: rng.float(0.2, 0.6),
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.confine.result`,
        },
      ];

    /** Das Echolot widerspricht der Karte. Neu vermessen oder weiterfahren. */
    case 'charted_wrong':
      return [
        {
          id: `${id}.sound`,
          labelKey: 'voyage.choice.sound',
          delayHours: rng.float(2, 4),
          fuelCost: rng.float(3, 9),
          yieldsEvidence: true,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.sound.result`,
        },
        {
          id: `${id}.trust_chart`,
          labelKey: 'voyage.choice.trust_chart',
          delayHours: 0,
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: rng.float(0, 4),
          outcomeKey: `${id}.trust_chart.result`,
        },
      ];

    case 'crew_remark':
      return [
        {
          id: `${id}.acknowledge`,
          labelKey: 'voyage.choice.acknowledge',
          delayHours: 0,
          fuelCost: 0,
          yieldsEvidence: false,
          soulsRescued: 0,
          hullDamage: 0,
          outcomeKey: `${id}.acknowledge.result`,
        },
      ];
  }
}

/**
 * Plant die Ereignisse einer Fahrt.
 *
 * Laengere Fahrten bringen mehr Ereignisse, aber nicht beliebig viele — sonst
 * wuerde die Ueberfahrt zur Ereigniskette statt zur Reise. Die Stellen liegen
 * im mittleren Teil der Route: direkt nach dem Ablegen und kurz vor der
 * Ankunft wuerde ein Abdrehen die Entscheidung entwerten.
 */
export function planVoyageEvents(rng: Rng, routeLengthNm: number): VoyageEvent[] {
  const count = Math.max(0, Math.min(4, Math.floor(routeLengthNm / 110)));
  if (count === 0) return [];

  const events: VoyageEvent[] = [];
  const eventRng = rng.derive('voyage-events');

  // Die Route in gleich grosse Abschnitte teilen und je Abschnitt eine Stelle
  // ziehen, damit sich Ereignisse nicht haeufen.
  for (let i = 0; i < count; i++) {
    const segmentStart = 0.15 + (i / count) * 0.7;
    const segmentEnd = 0.15 + ((i + 1) / count) * 0.7;

    const kind = eventRng.pickWeighted(KINDS, (k) => WEIGHTS[k]);
    const variant = eventRng.intInclusive(1, VARIANTS[kind]);
    const id = `voyage.event.${kind}.${variant}`;

    events.push({
      id: `${id}#${i}`,
      kind,
      textKey: id,
      atProgress: eventRng.float(segmentStart, segmentEnd),
      choices: choicesFor(kind, id, eventRng),
    });
  }

  return events.sort((a, b) => a.atProgress - b.atProgress);
}
